/** Google Consent Mode v2 — shared consent storage and update helpers. */
export const CONSENT_STORAGE_KEY = "rl-cookie-consent";
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
export const OPEN_COOKIE_SETTINGS_EVENT = "rl-open-cookie-settings";

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
  ts: number;
}

export const readConsent = (): ConsentChoice | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as ConsentChoice;
    if (!c || typeof c.ts !== "number" || Date.now() - c.ts > CONSENT_MAX_AGE_MS) return null;
    return { analytics: !!c.analytics, marketing: !!c.marketing, ts: c.ts };
  } catch {
    return null;
  }
};

type Gtag = (...args: unknown[]) => void;

export const hasAnalyticsConsent = (): boolean => readConsent()?.analytics === true;

const expireCookie = (name: string) => {
  const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/`;
  document.cookie = `${base}; domain=.retaillemon.com`;
  document.cookie = `${base}; domain=retaillemon.com`;
  document.cookie = base;
};

/** Borra cookies de Google Analytics y los identificadores propios de analítica. */
export const clearAnalyticsCookies = () => {
  if (typeof document === "undefined") return;
  try {
    const names = document.cookie
      .split(";")
      .map((c) => c.split("=")[0]?.trim() ?? "")
      .filter((n) => n === "_ga" || n.startsWith("_ga_") || n === "_gid" || n.startsWith("_gat"));
    for (const n of new Set([...names, "_ga", "_gid", "_gat"])) expireCookie(n);
    expireCookie("rl_vid");
    expireCookie("rl_channel");
    expireCookie("rl_channel_consumed");
    window.localStorage.removeItem("rl-public-session");
    window.sessionStorage.removeItem("rl-visit");
  } catch {
    /* noop */
  }
};

export const saveConsent = (analytics: boolean, marketing: boolean) => {
  const prev = readConsent();
  const choice: ConsentChoice = { analytics, marketing, ts: Date.now() };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
  } catch {
    /* almacenamiento no disponible */
  }
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: Gtag };
  w.dataLayer = w.dataLayer || [];
  const gtag: Gtag = w.gtag || function () { (w.dataLayer as unknown[]).push(arguments); };
  const ads = marketing ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analytics ? "granted" : "denied",
  });
  w.dataLayer.push({ event: "consent_update", consent_analytics: analytics, consent_marketing: marketing });
  window.dispatchEvent(new Event("consent_update"));
  if (!analytics) {
    clearAnalyticsCookies();
    if (prev?.analytics) window.location.reload();
  }
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};

/**
 * Inline head script: runs before GTM loads. Sets the denied defaults and
 * re-applies any stored (≤12 months) choice before any page view fires.
 */
export const CONSENT_DEFAULT_SCRIPT = `(function(){
window.dataLayer=window.dataLayer||[];
window.gtag=window.gtag||function(){dataLayer.push(arguments);};
gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'granted',security_storage:'granted',wait_for_update:500});
try{var c=JSON.parse(localStorage.getItem('${CONSENT_STORAGE_KEY}')||'null');
if(c&&typeof c.ts==='number'&&Date.now()-c.ts<=${CONSENT_MAX_AGE_MS}){var a=c.marketing?'granted':'denied';
gtag('consent','update',{ad_storage:a,ad_user_data:a,ad_personalization:a,analytics_storage:c.analytics?'granted':'denied'});}
else if(c){localStorage.removeItem('${CONSENT_STORAGE_KEY}');}}catch(e){}
})();`;
